import {computed, type ComputedRef, type Ref} from 'vue';

/** A single password requirement with its i18n label and live fulfillment state */
export interface PasswordRequirement {
  labelKey: string;
  met: boolean;
}

/** Zitadel rejects passwords of 70+ characters regardless of the complexity policy */
export const PASSWORD_MAX_LENGTH = 70;

/**
 * Live password-requirements checklist state, mirroring the Zitadel complexity
 * policy enforced by the BE (`password_policy_violation`). The regexes must stay
 * in sync with `usePasswordRules`.
 */
export function usePasswordRequirements(password: Ref<string>, confirmation: Ref<string>): {
  requirements: ComputedRef<PasswordRequirement[]>;
  allMet: ComputedRef<boolean>;
} {
  const requirements = computed((): PasswordRequirement[] => [
    {labelKey: 'password_required_8', met: password.value.length >= 8},
    {labelKey: 'password_required_less_70', met: password.value.length > 0 && password.value.length < PASSWORD_MAX_LENGTH},
    {labelKey: 'password_required_upper', met: /[A-Z]/u.test(password.value)},
    {labelKey: 'password_required_lower', met: /[a-z]/u.test(password.value)},
    {labelKey: 'password_required_number', met: /\d/u.test(password.value)},
    {labelKey: 'password_required_special', met: /[^A-Za-z0-9]/u.test(password.value)},
    {labelKey: 'password_confirmation_matched', met: password.value.length > 0 && password.value === confirmation.value},
  ]);

  const allMet = computed((): boolean => requirements.value.every(requirement => requirement.met));

  return {requirements, allMet};
}
